'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { fetchAllPaddles, type Paddle } from '@/lib/services/paddleService';

interface PaddlePageProps {
  params: Promise<{ slug: string }>;
}

function ScoreCircle({ score, size = 80 }: { score: number; size?: number }) {
  const radius = (size - 10) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={radius} fill="rgba(0,0,0,0.04)" stroke="none" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#D90012" strokeWidth="5"
          strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="bg-white rounded-full flex flex-col items-center justify-center" style={{ width: size * 0.72, height: size * 0.72 }}>
          <span className="font-black leading-none tabular-nums" style={{ fontSize: size * 0.26, color: '#0D0D0D' }}>{score}</span>
          <span className="leading-none" style={{ fontSize: size * 0.14, color: 'rgba(13,13,13,0.38)' }}>pts</span>
        </div>
      </div>
    </div>
  );
}

function StatBar({ label, value, max, unit = '' }: { label: string; value: number; max: number; unit?: string }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold" style={{ color: 'rgba(13,13,13,0.55)', letterSpacing: '-0.01em' }}>{label}</span>
        <span className="text-sm font-black tabular-nums" style={{ color: '#0D0D0D' }}>{value}{unit}</span>
      </div>
      <div className="score-bar-track">
        <div className="score-bar-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function PaddlePage({ params }: PaddlePageProps) {
  const { slug } = React.use(params);
  const [activeTab, setActiveTab] = useState('overview');
  const [showMoreSpecs, setShowMoreSpecs] = useState<Record<string, boolean>>({});
  const [compareWith, setCompareWith] = useState<string>('');
  const [paddle, setPaddle] = useState<Paddle | null>(null);
  const [relatedPaddles, setRelatedPaddles] = useState<Paddle[]>([]);
  const [displayRelated, setDisplayRelated] = useState<Paddle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllPaddles().then(allPaddles => {
      const found = allPaddles.find(p => p.slug === slug) ?? allPaddles[0];
      setPaddle(found);
      const related = allPaddles.filter(p => p.brand === found.brand && p.id !== found.id).slice(0, 5);
      const fallback = allPaddles.filter(p => p.id !== found.id).slice(0, 5);
      setRelatedPaddles(related);
      setDisplayRelated(related.length >= 3 ? related : fallback);
      setLoading(false);
    });
  }, [slug]);

  const tabs = ['OVERVIEW', 'SPECS', 'REVIEWS', 'PRICES'];

  const toggleMoreSpecs = (catName: string) => {
    setShowMoreSpecs(prev => ({ ...prev, [catName]: !prev[catName] }));
  };

  if (loading || !paddle) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8F8F8' }}>
        <Navigation />
        <div style={{ color: 'rgba(13,13,13,0.4)', fontSize: '14px' }}>Loading paddle…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F8F8F8' }}>
      <Navigation />

      {/* Tab navigation */}
      <div className="tab-nav" style={{ top: '64px' }}>
        <div className="max-w-container mx-auto px-4 flex items-center justify-between w-full">
          <div className="flex">
            {tabs.map(tab => (
              <button
                key={tab}
                className={`tab-btn ${activeTab === tab.toLowerCase() ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.toLowerCase())}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-2">
            <select
              value={compareWith}
              onChange={e => setCompareWith(e.target.value)}
              className="text-sm rounded-lg px-3 py-1.5 focus:outline-none"
              style={{
                border: '1px solid rgba(0,0,0,0.09)',
                color: '#0D0D0D',
                background: '#fff',
                fontFamily: 'inherit',
                transition: 'border-color 0.18s ease',
              }}
              onFocus={e => { e.currentTarget.style.borderColor = 'rgba(217,0,18,0.38)'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'rgba(0,0,0,0.09)'; }}
            >
              <option value="">Compare with...</option>
              {displayRelated.map(p => (
                <option key={p.id} value={p.slug}>{p.name}</option>
              ))}
            </select>
            {compareWith && (
              <Link href={`/compare/${paddle.slug}-vs-${compareWith}`} className="versusButton text-sm">
                Compare
              </Link>
            )}
          </div>
        </div>
      </div>

      <main className="max-w-container mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <div className="breadcrumb mb-5">
          <Link href="/" style={{ textDecoration: 'none' }}>Home</Link>
          <span className="breadcrumb-sep">/</span>
          <Link href="/categories" style={{ textDecoration: 'none' }}>Paddles</Link>
          <span className="breadcrumb-sep">/</span>
          <span style={{ color: 'rgba(13,13,13,0.55)' }}>{paddle.name}</span>
        </div>

        {/* Hero section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
          {/* Image */}
          <div
            className="relative rounded-2xl overflow-hidden"
            style={{ minHeight: 320, background: 'linear-gradient(145deg, #F8F8F8 0%, #F0F0F0 100%)' }}
          >
            {paddle.isNew && (
              <div className="absolute top-4 left-4 z-10">
                <span className="text-white text-xs font-black px-3 py-1.5 rounded-full" style={{ background: '#D90012', letterSpacing: '0.06em' }}>NEW {paddle.releaseYear}</span>
              </div>
            )}
            {paddle.firepowerTier && (
              <div className="absolute top-4 right-4 z-10">
                <span className="text-white text-xs font-black px-3 py-1.5 rounded-full" style={{ background: '#0D0D0D', letterSpacing: '0.04em' }}>{paddle.firepowerTier}</span>
              </div>
            )}
            <div className="absolute inset-0 flex items-center justify-center">
              <img src={paddle.image} alt={paddle.imageAlt} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }} />
            </div>
          </div>

          {/* Info */}
          <div>
            <div className="flex items-start gap-4 mb-6">
              <ScoreCircle score={paddle.score} />
              <div>
                <div className="text-xs font-black mb-1 uppercase tracking-widest" style={{ color: 'rgba(13,13,13,0.36)', letterSpacing: '0.12em' }}>{paddle.brand}</div>
                <h1 className="text-2xl md:text-3xl font-black" style={{ color: '#0D0D0D', letterSpacing: '-0.035em', lineHeight: 1.12 }}>{paddle.name}</h1>
                <p className="mt-2 text-sm" style={{ color: 'rgba(13,13,13,0.5)', lineHeight: 1.7 }}>{paddle.tagline}</p>
              </div>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-2 mb-6">
              {paddle.approvalBody && (
                <span className="badge-neutral">
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }}>
                    <polyline points="2 6 5 9 10 3" />
                  </svg>
                  {paddle.approvalBody}
                </span>
              )}
              {paddle.shape && (
                <span className="badge-neutral">{paddle.shape}</span>
              )}
              {paddle.paddleType && (
                <span className="badge-red">{paddle.paddleType}</span>
              )}
            </div>

            {/* Price */}
            <div className="text-3xl font-black mb-6 tabular-nums" style={{ color: '#0D0D0D', letterSpacing: '-0.04em' }}>{paddle.priceFormatted}</div>

            {/* Key stats */}
            {(paddle.spin || paddle.power || paddle.pop) && (
              <div className="mb-6 p-4 rounded-2xl" style={{ background: '#F8F8F8', border: '1px solid rgba(0,0,0,0.06)' }}>
                {paddle.spin ? <StatBar label="Spin" value={paddle.spin} max={2200} unit=" RPM" /> : null}
                {paddle.power ? <StatBar label="Power" value={paddle.power} max={60} unit=" MPH" /> : null}
                {paddle.pop ? <StatBar label="Pop" value={paddle.pop} max={40} unit=" MPH" /> : null}
              </div>
            )}

            {/* Quick specs */}
            <div className="grid grid-cols-3 gap-2.5 mb-6">
              {paddle.coreThickness && (
                <div className="rounded-xl p-3.5 text-center" style={{ background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                  <div className="text-lg font-black tabular-nums" style={{ color: '#0D0D0D', letterSpacing: '-0.03em' }}>{paddle.coreThickness}mm</div>
                  <div className="text-xs mt-0.5" style={{ color: 'rgba(13,13,13,0.42)' }}>Core</div>
                </div>
              )}
              {paddle.weight && (
                <div className="rounded-xl p-3.5 text-center" style={{ background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                  <div className="text-lg font-black tabular-nums" style={{ color: '#0D0D0D', letterSpacing: '-0.03em' }}>{paddle.weight}oz</div>
                  <div className="text-xs mt-0.5" style={{ color: 'rgba(13,13,13,0.42)' }}>Weight</div>
                </div>
              )}
              {paddle.swingweight && (
                <div className="rounded-xl p-3.5 text-center" style={{ background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                  <div className="text-lg font-black tabular-nums" style={{ color: '#0D0D0D', letterSpacing: '-0.03em' }}>{paddle.swingweight}</div>
                  <div className="text-xs mt-0.5" style={{ color: 'rgba(13,13,13,0.42)' }}>Swingweight</div>
                </div>
              )}
            </div>

            {/* CTA buttons */}
            <div className="flex flex-wrap gap-3">
              <a href="#" className="versusButton pill flex-1 text-center">Check Price</a>
              <Link
                href="/compare"
                className="btn-ghost flex-1 text-center"
                style={{ borderRadius: 'var(--radius-pill)', padding: '10px 24px' }}
              >
                Add to Compare
              </Link>
            </div>
          </div>
        </div>

        {/* Related paddles */}
        {displayRelated.length > 0 && (
          <section className="mt-12">
            <h2 className="section-heading mb-5">
              {relatedPaddles.length >= 3 ? `More from ${paddle.brand}` : 'You might also like'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {displayRelated.map(p => (
                <Link
                  key={p.id}
                  href={`/paddles/${p.slug}`}
                  className="card-base rounded-xl overflow-hidden"
                  style={{ textDecoration: 'none', display: 'block' }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(217,0,18,0.16)';
                    (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                    (e.currentTarget as HTMLElement).style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,0,0,0.07)';
                    (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                    (e.currentTarget as HTMLElement).style.boxShadow = 'var(--shadow-card)';
                  }}
                >
                  <div className="relative" style={{ paddingBottom: '80%', background: 'rgba(0,0,0,0.02)' }}>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <img src={p.image} alt={p.imageAlt} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }} />
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="text-xs font-semibold mb-1" style={{ color: '#0D0D0D', lineHeight: 1.35 }}>{p.name}</div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold tabular-nums" style={{ color: '#0D0D0D' }}>{p.priceFormatted}</span>
                      <span className="text-xs font-bold tabular-nums" style={{ color: '#D90012' }}>{p.score}pts</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
